<script>
import Vue from 'vue'

import {
  mapActions,
  mapGetters
} from 'vuex'

import firebase from 'firebase/app'
import * as firebaseui from 'firebaseui'

import CandelaLogo from '@/assets/svg/candela-logo.svg?inline'
import {init_firebase, firebase_ui_config} from '@/utils/firebase'
import {alpha_num, has_value} from '@/utils/validators'
import {ReqOps, ReqState} from '@/utils/vars'

export default {
  components: {
    CandelaLogo
  },
  computed: {
    ...mapGetters([
      'namespace',
      'req_errors',
      'req_state',
      'user',
      'user_abbr'
    ]),
    copy_icon() {
      return this.id_copied ?
        `fa-clipboard-check` : `far fa-clipboard`
    },
    create_complete() {
      return this.req_state[ReqOps.CreateApp] === ReqState.Complete
    },
    create_error() {
      return this.req_state[ReqOps.CreateApp] === ReqState.Error
    },
    create_error_str() {
      return this.req_errors[ReqOps.CreateApp]
    },
    create_in_progress() {
      return this.req_state[ReqOps.CreateApp] === ReqState.Request
    },
    create_ready() {
      return this.req_state[ReqOps.CreateApp] === ReqState.None
    },
    login_dialog_is_open() { return this.user === null }
  },
  data() {
    return {
      alpha_num,
      create_app_dialog_is_open: false,
      form_data: {},
      has_value,
      id_copied: false,
      initialized: false,
      login_open: true,
      new_app: null
    }
  },
  methods: {
    ...mapActions([
      'clear_req_op',
      'connect',
      'create_app',
      'init_app',
      'login',
      'logout',
      'set_app_size'
    ]),
    copy_app_id() {
      const app_id = this.$refs.app_id_text
      app_id.setAttribute('type', 'text')
      app_id.select()
      app_id.setSelectionRange(0, 99999)
      document.execCommand('copy')
      app_id.setAttribute('type', 'hidden')
      this.id_copied = true
    },
    create_new_app() {
      const form = this.$refs.form
      if(form && form.validate()) {
        this.create_app(this.form_data)
          .then(app => {
            this.id_copied = false
            this.new_app = app
          })
      }
    },
    on_auth_change(user) {
      if(user) {
        this.login(user)
      } else {
        this.logout()
        this.login_ui.start('#firebaseui', firebase_ui_config())
      }
    },
    on_connect_click() {
      this.connect(this.new_app.id)
        .then(() => {
          this.create_app_dialog_is_open = false
        })
    },
    on_logout_click() {
      firebase.auth().signOut()
    },
    on_resize(x, y, z) {
      this.set_app_size([window.innerWidth, window.innerHeight])
    },
    open_create_dialog() {
      const form = this.$refs.form
      if(form) form.reset()
      this.form_data = {title: '', description: ''}
      this.clear_req_op(ReqOps.CreateApp)
      this.create_app_dialog_is_open = true
    }
  },
  mounted() {
    this.init_app()
    this.auth = init_firebase(this.$router, this.on_auth_change)
    Vue.nextTick(() => {
      if(this.login_dialog_is_open) {
        let ui = firebaseui.auth.AuthUI.getInstance()
        if(!ui) {
          this.login_ui = new firebaseui.auth.AuthUI(this.auth)
          this.login_ui.start('#firebaseui', firebase_ui_config())
        }
      }
    })
  },
  name: 'App'
}
</script>

<template>
  <v-app v-resize='on_resize'>
    <v-app-bar v-if='user' class='app-bar pr-0' elevation='0'  app>
      <div class='d-flex align-start logo-container'>
        <CandelaLogo class='logo'/>
        <v-toolbar-title class='app-title display-1 font-weight-bold ml-3'>
          Candela
        </v-toolbar-title>
      </div>
      <v-spacer/>
      <v-btn @click='open_create_dialog' class='mr-4'
        color='primary' text>
        <v-icon class='mr-2' x-small>fa-plus</v-icon>
        <span>New App</span>
      </v-btn>
      <div class='d-flex align-center'>
        <v-menu offset-y open-on-hover>
          <template v-slot:activator='{ on }'>
            <v-btn v-on='on' icon small>
              <v-avatar color='primary' size='32'>
                <img v-if='user && user.avatar' :src='user.avatar'/>
                <div v-if='user && !user.avatar' class='body-1'>{{user_abbr}}</div>
              </v-avatar>
            </v-btn>
          </template>
          <v-card>
            <v-list>
              <v-list-item @click='on_logout_click'>
                <v-icon class='mr-2' small>fa-sign-out-alt</v-icon>
                <span>Logout</span>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </div>
    </v-app-bar>

    <v-container fluid class='d-flex flex-grow-1 pa-0'>
      <router-view/>
    </v-container>

    <!-- Login Dialog -->
    <v-dialog v-model='login_dialog_is_open' width='unset' persistent>
      <v-card outlined>
        <div class='d-flex flex-column align-center'>
          <div ref='ui' id='firebaseui'/>
        </div>
      </v-card>
    </v-dialog>

    <!-- App Create Dialog -->
    <v-dialog v-model='create_app_dialog_is_open' max-width='640' min-width='340'>
      <!-- Initial App Creation Form -->
      <v-card v-if='create_ready' class='pa-4'>
        <div class='headline mb-2 primary--text'>Create a New App</div>
        <v-form ref='form' class='mb-4'>
          <v-text-field v-model='form_data.title' label='App Name' class='mb-2'
            :rules='[alpha_num, has_value]' required dense outlined/>
          <v-textarea v-model='form_data.description' label='Description'
            dense hide-details outlined/>
        </v-form>
        <div class='d-flex justify-end'>
          <v-btn @click='create_app_dialog_is_open=false' class='mr-2'
            color='error' outlined small>
            <v-icon left small>fa-times</v-icon>
            <span>Cancel</span>
          </v-btn>
          <v-btn @click='create_new_app'
            color='primary' outlined small>
            <v-icon left small>fa-plus</v-icon>
            <span>Create</span>
          </v-btn>
        </div>
      </v-card>

      <!-- App Create Progress -->
      <v-card v-if='create_in_progress' class='pa-4'>
        <div class='headline mb-4 primary--text'>Creating...</div>
        <v-progress-linear class='mb-2' indeterminate/>
      </v-card>

      <!-- App Create Success -->
      <v-card v-if='create_complete && new_app' class='pa-4'>
        <div class='headline font-weight-bold primary--text mb-2'>
          App Created!
        </div>
        <div class='body-1'>
          This is the ID for your new Candela app.
          Use this ID in your client configuration file.
        </div>
        <div @click='copy_app_id' class='d-flex align-center body-1 mt-4'>
          <v-icon class='mr-2 my-1' color='orange' v-text='copy_icon' small/>
            {{new_app.id}}
          <input ref='app_id_text' type='hidden' :value='new_app.id'>
        </div>
        <span @click='copy_app_id' class='overline mt-1 mb-4'>
          {{id_copied ? `Copied!` : `Click to copy`}}
        </span>
        <div class='d-flex justify-end mt-4'>
          <v-btn @click='on_connect_click' color='primary' outlined>Connect Now</v-btn>
        </div>
      </v-card>

      <!-- App Create Error -->
      <v-card v-if='create_error' class='pa-6'>
        <div class='d-flex align-center'>
          <v-icon class='mr-2' color='error' small>fa-exclamation-triangle</v-icon>
          <span class='headline error--text'>
            Error Creating App
          </span>
        </div>
        <div class='title'>
          {{create_error_str}}
        </div>
        <div class='d-flex justify-end'>
          <v-btn @click='open_create_dialog' class='mt-4' outlined>Try again</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<style lang='sass' scoped>
a
  text-decoration: none

.app-title
  letter-spacing: -0.05em !important

.logo-container:hover
  color: var(--v-secondary-base)

.logo
  width: 24px

.theme--dark
  .app-title
    color: var(--v-primary-base) !important

  .app-bar
    border-bottom: 1px solid var(--v-grey-darken6)

.theme--light
  .app-title
    color: var(--v-secondary-base) !important

  .app-bar
    border-bottom: 1px solid var(--v-grey-darken8)
</style>
